import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowLeft,
  Pencil,
  Trash,
  PaperPlaneTilt,
  Copy,
  Warning,
  Download,
  FilePdf,
  Archive,
  TrayArrowDown,
  PaperPlane,
  Plus
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enGB } from 'date-fns/locale';
import api from '../../../utils/api';
import useThemeStyles from '../../../theme/useThemeStyles';
import StatusBadge from './StatusBadge';
import PaymentLinkModal from './PaymentLinkModal';
import ManualPaymentWarning from './ManualPaymentWarning';

function InvoiceDetail({ readOnly = false }) {
  const styles = useThemeStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [_stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [resending, setResending] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    receivedAt: new Date(),
    method: 'bank_transfer',
    reference: '',
    notes: ''
  });

  const fetchPayments = useCallback(async () => {
    try {
      const response = await api.get(`/api/invoices/${id}/payments`);
      setPayments(response.data.payments || []);
      setPaymentSummary(response.data.summary || null);
    } catch (error) {
      // Payments are supplementary; don't block the invoice view
      setPayments([]);
      setPaymentSummary(null);
    }
  }, [id]);

  const fetchInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/invoices/${id}`);
      setInvoice(response.data);
      
      // Check Stripe Connect status if business is populated
      const businessId = response.data.business?._id || response.data.business;
      if (businessId) {
        checkStripeConnectStatus(businessId);
      }
    } catch (error) {
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
    fetchPayments();
  }, [fetchInvoice, fetchPayments]);

  const checkStripeConnectStatus = async (businessId) => {
    try {
      if (!businessId) return;
      const response = await api.get(`/api/stripe-connect/status/${businessId}`);
      setStripeConnectStatus(response.data);
    } catch (error) {
      // Ignore errors
      setStripeConnectStatus(null);
    }
  };

  const handleSend = async () => {
    try {
      await api.post(`/api/invoices/${id}/send`);
      fetchInvoice();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send invoice');
    }
  };

  const handleResend = async () => {
    try {
      setResending(true);
      setError('');
      await api.post(`/api/invoices/${id}/resend`);
      fetchInvoice();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to resend invoice');
    } finally {
      setResending(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.delete(`/api/invoices/${id}`);
      navigate('/supplier/invoices');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete invoice');
    }
  };

  const _handleGeneratePaymentLink = async () => {
    try {
      const response = await api.post(`/api/invoices/${id}/generate-payment-link`);
      setInvoice({ ...invoice, ...response.data.invoice });
      setPaymentLinkModalOpen(true);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to generate payment link');
    }
  };

  const handleTogglePaid = async () => {
    try {
      await api.post(`/api/invoices/${id}/toggle-paid`);
      fetchInvoice();
      fetchPayments();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update payment status');
    }
  };

  const openPaymentDialog = () => {
    setPaymentForm({
      amount: paymentSummary?.amountOutstanding ?? '',
      receivedAt: new Date(),
      method: 'bank_transfer',
      reference: '',
      notes: ''
    });
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = async () => {
    try {
      setSavingPayment(true);
      setError('');
      await api.post(`/api/invoices/${id}/payments`, {
        amount: parseFloat(paymentForm.amount),
        receivedAt: paymentForm.receivedAt,
        method: paymentForm.method,
        reference: paymentForm.reference || undefined,
        notes: paymentForm.notes || undefined
      });
      setPaymentDialogOpen(false);
      fetchInvoice();
      fetchPayments();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to record payment');
      setPaymentDialogOpen(false);
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await api.delete(`/api/invoices/${id}/payments/${paymentId}`);
      fetchInvoice();
      fetchPayments();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete payment');
    }
  };

  const paymentMethodLabels = {
    manual: 'Manual',
    bank_transfer: 'Bank transfer',
    cash: 'Cash',
    card: 'Card',
    cheque: 'Cheque',
    stripe: 'Card (Stripe)',
    other: 'Other'
  };

  const handleArchive = async () => {
    try {
      await api.post(`/api/invoices/${id}/archive`);
      fetchInvoice();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to archive invoice');
    }
  };

  const handleUnarchive = async () => {
    try {
      await api.post(`/api/invoices/${id}/unarchive`);
      fetchInvoice();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to unarchive invoice');
    }
  };

  const handleDownloadPDF = async () => {
    if (!invoice) return;

    setPdfGenerating(true);
    try {
      const response = await api.get(`/api/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download PDF. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Alert severity="error">
        Invoice not found
      </Alert>
    );
  }

  const downloadButton = isMobile ? (
    <Tooltip title={pdfGenerating ? 'Generating...' : 'Download PDF'}>
      <span>
        <IconButton
          onClick={handleDownloadPDF}
          disabled={pdfGenerating}
          size="medium"
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <Download size={20} />
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    <Button
      startIcon={<FilePdf size={18} />}
      onClick={handleDownloadPDF}
      disabled={pdfGenerating}
      variant="outlined"
    >
      {pdfGenerating ? 'Generating...' : 'Download PDF'}
    </Button>
  );

  const archiveButton = !readOnly && (invoice.archived ? (
    <Button
      startIcon={<TrayArrowDown size={18} />}
      onClick={handleUnarchive}
      variant="outlined"
    >
      Unarchive
    </Button>
  ) : (
    <Button
      startIcon={<Archive size={18} />}
      onClick={handleArchive}
      variant="outlined"
    >
      Archive
    </Button>
  ));

  const canAddExtras = !readOnly && ['sent', 'partially_paid', 'overdue', 'paid'].includes(invoice.status);
  const invoicePath = (invoiceId) => (readOnly ? `/invoice/${invoiceId}` : `/supplier/invoices/${invoiceId}`);
  const addToButton = canAddExtras ? (
    <Button
      startIcon={<Plus size={18} />}
      onClick={() => navigate(`/supplier/invoices/new?addTo=${id}`)}
      variant="contained"
      size={isMobile ? 'small' : 'medium'}
    >
      Add to invoice
    </Button>
  ) : null;

  const actionsUnderTitle = isMobile && (
    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
      {addToButton}
      {downloadButton}
      {!readOnly && invoice.status === 'sent' && (
        <Button
          startIcon={<PaperPlane size={18} />}
          onClick={handleResend}
          disabled={resending}
          variant="outlined"
          size="small"
        >
          {resending ? 'Resending...' : 'Resend'}
        </Button>
      )}
      {!readOnly && invoice.paymentStatus.isPaid && invoice.paymentStatus.manuallyMarkedPaid && !invoice.paymentStatus.stripeConfirmed && (
        <Button
          startIcon={<Warning size={18} />}
          onClick={handleTogglePaid}
          color="warning"
          size="small"
          variant="outlined"
        >
          Unmark as Paid
        </Button>
      )}
    </Box>
  );

  const headerButtons = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {addToButton}
      {!isMobile && downloadButton}
      {!readOnly && (invoice.status === 'draft' || invoice.status === 'scheduled') && (
        <>
          <Button
            startIcon={<Pencil size={18} />}
            onClick={() => navigate(`/supplier/invoices/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            startIcon={<PaperPlaneTilt size={18} />}
            onClick={handleSend}
          >
            Send Invoice
          </Button>
          <Button
            color="error"
            startIcon={<Trash size={18} />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </>
      )}
      {!readOnly && invoice.status === 'sent' && !isMobile && (
        <Button
          startIcon={<PaperPlane size={18} />}
          onClick={handleResend}
          disabled={resending}
          variant="outlined"
        >
          {resending ? 'Resending...' : 'Resend'}
        </Button>
      )}
      {!readOnly && invoice.paymentStatus.isPaid && invoice.paymentStatus.manuallyMarkedPaid && !invoice.paymentStatus.stripeConfirmed && (
        <Button
          startIcon={<Warning size={18} />}
          onClick={handleTogglePaid}
          color="warning"
          variant="outlined"
        >
          Unmark as Paid
        </Button>
      )}
      {!isMobile && archiveButton}
    </Box>
  );

  return (
    <Box sx={styles.container.page}>
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(readOnly ? '/dashboard' : '/supplier/invoices')} sx={{ mr: 1 }}>
            <ArrowLeft size={24} />
          </IconButton>
          <Box>
            <Typography component="h2" className="pagename" sx={styles.text.pagename}>
              Invoice {invoice.invoiceNumber}
            </Typography>
            <StatusBadge status={invoice.status} />
            {invoice.sourceInvoice?.invoiceNumber && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Extras for{' '}
                <Box
                  component="button"
                  type="button"
                  onClick={() => navigate(invoicePath(invoice.sourceInvoice._id))}
                  sx={{
                    border: 0,
                    p: 0,
                    background: 'none',
                    color: 'primary.main',
                    cursor: 'pointer',
                    font: 'inherit'
                  }}
                >
                  {invoice.sourceInvoice.invoiceNumber}
                </Box>
              </Typography>
            )}
            {actionsUnderTitle}
          </Box>
        </Box>

        {!isMobile && headerButtons}
        {isMobile && (invoice.status === 'draft' || invoice.status === 'scheduled') && !readOnly && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            {headerButtons}
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {invoice.paymentStatus.stripeConfirmed && invoice.status === 'sent' && (
        <ManualPaymentWarning />
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Invoice Details
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Client
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {invoice.manualRecipient?.name
                    ? `${invoice.manualRecipient.name} (${invoice.manualRecipient.email})`
                    : invoice.wedding?.name ||
                      (invoice.wedding?.partnerDetails?.partnerOne?.name && invoice.wedding?.partnerDetails?.partnerTwo?.name
                        ? `${invoice.wedding.partnerDetails.partnerOne.name} & ${invoice.wedding.partnerDetails.partnerTwo.name}`
                        : invoice.wedding?.partnerDetails?.partnerOne?.name || 'N/A')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Due Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {invoice.dueDate ? format(new Date(invoice.dueDate), 'dd MMM yyyy') : '-'}
                </Typography>
              </Grid>
              {invoice.scheduledSendAt && (invoice.status === 'scheduled' || invoice.status === 'draft') && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Scheduled to send
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {format(new Date(invoice.scheduledSendAt), 'dd MMM yyyy')}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">
                  {invoice.description || '-'}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 2 }}>
              Line Items
            </Typography>
            {invoice.lineItems.map((item, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {item.description}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {item.quantity} × {formatCurrency(
                      (item.subtotal ?? (item.quantity || 1) * (item.unitPrice || 0)) / (item.quantity || 1),
                      invoice.currency
                    )}
                    {item.taxPercentage > 0 && ' (excl. VAT)'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal: {formatCurrency(item.subtotal || (item.quantity || 1) * (item.unitPrice || 0), invoice.currency)}
                  </Typography>
                </Box>
                {item.taxPercentage > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Tax ({item.taxPercentage}%): {formatCurrency(item.taxAmount || 0, invoice.currency)}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Total:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatCurrency(item.total, invoice.currency)}
                  </Typography>
                </Box>
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'right' }}>
              {(() => {
                const subtotal = invoice.lineItems.reduce((sum, item) => sum + (item.subtotal || (item.quantity || 1) * (item.unitPrice || 0)), 0);
                const totalTax = invoice.lineItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0);
                
                return (
                  <>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Subtotal: {formatCurrency(subtotal, invoice.currency)}
                      </Typography>
                    </Box>
                    {totalTax > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Tax: {formatCurrency(totalTax, invoice.currency)}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Typography variant="h6">Total Amount</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {formatCurrency(invoice.amount, invoice.currency)}
                      </Typography>
                    </Box>
                  </>
                );
              })()}
            </Box>
          </Paper>

          {invoice.paymentSchedule?.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment Schedule
              </Typography>
              {invoice.paymentSchedule.map((item, index) => (
                <Box
                  key={item._id || index}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: index < invoice.paymentSchedule.length - 1 ? '1px solid rgba(0,0,0,0.08)' : 'none' }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.label}
                    </Typography>
                    {item.dueDate && (
                      <Typography variant="caption" color="text.secondary">
                        Due {format(new Date(item.dueDate), 'dd MMM yyyy')}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatCurrency(item.amount, invoice.currency)}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          {(payments.length > 0 || (!readOnly && ['sent', 'partially_paid', 'overdue', 'paid'].includes(invoice.status))) && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Payments Received
                </Typography>
                {!readOnly && !invoice.paymentStatus.isPaid && ['sent', 'partially_paid', 'overdue'].includes(invoice.status) && (
                  <Button size="small" variant="outlined" onClick={openPaymentDialog}>
                    Record Payment
                  </Button>
                )}
              </Box>
              {payments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No payments recorded yet.
                </Typography>
              ) : (
                payments.map((payment, index) => (
                  <Box
                    key={payment._id}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: index < payments.length - 1 ? '1px solid rgba(0,0,0,0.08)' : 'none' }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(payment.amount, payment.currency || invoice.currency)}
                        {payment.status !== 'succeeded' && ` (${payment.status})`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(payment.receivedAt), 'dd MMM yyyy')} · {paymentMethodLabels[payment.method] || payment.method}
                        {payment.reference ? ` · Ref: ${payment.reference}` : ''}
                      </Typography>
                      {payment.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {payment.notes}
                        </Typography>
                      )}
                    </Box>
                    {!readOnly && payment.method !== 'stripe' && !payment.stripePaymentIntentId && (
                      <IconButton size="small" onClick={() => handleDeletePayment(payment._id)} aria-label="Delete payment">
                        <Trash size={16} />
                      </IconButton>
                    )}
                  </Box>
                ))
              )}
            </Paper>
          )}

          {invoice.paymentLink?.url && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment Link
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Copy size={18} />}
                  onClick={() => {
                    navigator.clipboard.writeText(invoice.paymentLink.url);
                  }}
                >
                  Copy Link
                </Button>
                {invoice.paymentLink.expiresAt && (
                  <Typography variant="body2" color="text.secondary">
                    Expires: {format(new Date(invoice.paymentLink.expiresAt), 'dd MMM yyyy HH:mm')}
                  </Typography>
                )}
              </Box>
              <Alert severity="info">
                Share this payment link with the client. They can pay securely via Stripe.
              </Alert>
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Payment Information
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Payment Status
                </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {invoice.paymentStatus.isPaid
                  ? 'Paid'
                  : (paymentSummary?.totalPaid > 0 ? 'Partially paid' : 'Unpaid')}
              </Typography>
              {invoice.paymentStatus.isPaid && invoice.paymentStatus.paidAt && (
                <Typography variant="body2" color="text.secondary">
                  Paid on: {format(new Date(invoice.paymentStatus.paidAt), 'dd MMM yyyy')}
                </Typography>
              )}
            </Box>

            {paymentSummary && paymentSummary.totalPaid > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Paid so far
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatCurrency(paymentSummary.totalPaid, invoice.currency)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Outstanding balance
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(paymentSummary.amountOutstanding, invoice.currency)}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Payment Method
                </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {invoice.paymentMethod === 'payment_link' ? (
                  <Chip label="Payment Link" size="small" color="success" />
                ) : invoice.paymentMethod === 'manual' ? (
                  <Chip label="Manual" size="small" />
                ) : (
                  '-'
                )}
              </Typography>
            </Box>

            {invoice.paymentStatus.stripeConfirmed && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Payment confirmed by Stripe
              </Alert>
            )}

            {invoice.paymentStatus.manuallyMarkedPaid && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Marked as paid manually by supplier
              </Alert>
            )}

            {/* Fee breakdown temporarily hidden - payments feature coming soon */}
            {/* {stripeConnectStatus?.connected && stripeConnectStatus?.chargesEnabled && (
              <FeeBreakdown
                fees={invoice.fees}
                amount={invoice.amount}
                currency={invoice.currency}
              />
            )} */}
          </Paper>

          {invoice.invoiceGroup && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Booking total
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Original invoice plus any extras added later.
              </Typography>
              {[
                ['Original invoice', invoice.invoiceGroup.originalAmount],
                ['Extras added', invoice.invoiceGroup.extrasAmount],
                ['Booking total', invoice.invoiceGroup.totalAmount],
                ['Paid', invoice.invoiceGroup.paidAmount],
                ['Still to pay', invoice.invoiceGroup.outstandingAmount]
              ].map(([label, amount]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: label === 'Booking total' || label === 'Still to pay' ? 600 : 400 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: label === 'Booking total' || label === 'Still to pay' ? 600 : 400 }}>
                    {formatCurrency(amount, invoice.invoiceGroup.currency || invoice.currency)}
                  </Typography>
                </Box>
              ))}
              <Divider sx={{ my: 2 }} />
              {invoice.invoiceGroup.invoices.map((item) => (
                <Box key={item._id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, gap: 1 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => navigate(invoicePath(item._id))}
                    sx={{
                      border: 0,
                      p: 0,
                      background: 'none',
                      color: 'primary.main',
                      cursor: 'pointer',
                      font: 'inherit',
                      textAlign: 'left'
                    }}
                  >
                    {item.invoiceNumber}
                    {item.isOriginal ? ' (original)' : ' (extras)'}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusBadge status={item.status} />
                    <Typography variant="body2">
                      {formatCurrency(item.amount, invoice.invoiceGroup.currency || invoice.currency)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>
          )}
        </Grid>
      </Grid>

      {isMobile && archiveButton && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          {archiveButton}
        </Box>
      )}

      <PaymentLinkModal
        open={paymentLinkModalOpen}
        onClose={() => setPaymentLinkModalOpen(false)}
        paymentLink={invoice.paymentLink?.url}
        expiresAt={invoice.paymentLink?.expiresAt}
        invoiceNumber={invoice.invoiceNumber}
      />

      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {paymentSummary && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Outstanding balance: {formatCurrency(paymentSummary.amountOutstanding, invoice.currency)}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={paymentForm.amount}
            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
            inputProps={{ min: 0.01, step: 0.01 }}
            sx={{ mb: 2, mt: 1 }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <DatePicker
              label="Date received"
              value={paymentForm.receivedAt}
              onChange={(value) => setPaymentForm({ ...paymentForm, receivedAt: value })}
              slotProps={{ textField: { fullWidth: true, sx: { mb: 2 } } }}
            />
          </LocalizationProvider>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Payment method</InputLabel>
            <Select
              value={paymentForm.method}
              label="Payment method"
              onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
            >
              <MenuItem value="bank_transfer">Bank transfer</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Reference (optional)"
            value={paymentForm.reference}
            onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Notes (optional)"
            value={paymentForm.notes}
            onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRecordPayment}
            disabled={savingPayment || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0}
          >
            {savingPayment ? 'Saving...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InvoiceDetail;
